/**
 * executionFeedback.ts — MER Algo Footprints v8.8.1
 *
 * Execution Feedback Loop.
 *
 * Closes the decision loop: Plan → Execute (human) → Evaluate → Improve.
 *
 * ── Design rules ──────────────────────────────────────────────────────────────
 * 1. Pure function — no Date.now() in semantic output, deterministic
 * 2. Reads only — never mutates plan or journal data
 * 3. Explainable — every score component has a named rule + reason string
 * 4. Replay-safe — serializable inputs/outputs
 * 5. Conservative — never claims certainty beyond what data supports
 *
 * ── Input sources ────────────────────────────────────────────────────────────
 *   TradePlan   — from executionIntelligence.buildExecutionPlan()
 *   JournalEntry — operator-recorded actual trade (entry, stop, exit, rMultiple)
 *
 * ── Output hierarchy ─────────────────────────────────────────────────────────
 *   PlanOutcomeComparison — did the plan play out as expected?
 *   EntryQuality          — how well was the entry executed?
 *   RiskAdherence         — was risk respected?
 *   TargetAchievement     — how far did the trade go?
 *   MissedOpportunity     — was a valid setup ignored?
 *   ExecutionScore        — composite 0–100 execution quality score
 *   ExecutionFeedback     — all of the above + actionable guidance
 */

import type { TradePlan }    from './executionIntelligence';
import type { JournalEntry } from '../../shared/types/index';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const FEEDBACK_THRESHOLDS = {
  // Entry zone tolerance: price within this % of idealEntry = "optimal" entry
  ENTRY_OPTIMAL_PCT:      0.003,    // 0.3%
  // Favorable/unfavorable: within 2× zone width is "neutral"
  ENTRY_NEUTRAL_PCT:      0.008,    // 0.8%

  // Missed entry: if exit == null and rMultiple == null → likely not taken
  // "Late" timing: actual entry > 0.5% beyond idealEntry in the wrong direction
  ENTRY_LATE_PCT:         0.005,    // 0.5%

  // Stop breach: actual exit below/above stop (by this tolerance) → violation
  STOP_BREACH_TOLERANCE:  0.001,    // 0.1% — accounts for slippage

  // Target hit: price came within this % of target = "hit"
  TARGET_HIT_PCT:         0.005,    // 0.5%

  // Efficiency: how much of the available move was captured (rMultiple / maxRR)
  EFFICIENCY_EXCELLENT:   0.80,
  EFFICIENCY_GOOD:        0.50,

  // Score component weights (must sum to 1.0)
  W_ENTRY_QUALITY:        0.30,
  W_TIMING:               0.20,
  W_ADHERENCE:            0.25,
  W_OUTCOME_ALIGNMENT:    0.25,
} as const;

const T = FEEDBACK_THRESHOLDS;

// ── Output types ──────────────────────────────────────────────────────────────

export type OutcomeClassification =
  | 'target_hit'
  | 'stopped_out'
  | 'partial'
  | 'invalidated'
  | 'missed'
  | 'unknown';

export interface PlanOutcomeComparison {
  planId:     string;         // plan.symbol + setup type + direction
  followed:   boolean;        // was the plan actually taken?
  outcome:    OutcomeClassification;
  rMultiple:  number | null;
  /** Specific deviations from the plan */
  deviation:  string[];
}

export type EntryTiming    = 'early' | 'optimal' | 'late' | 'missed';
export type PriceQuality   = 'favorable' | 'neutral' | 'unfavorable';
export type EntryAdherence = 'followed' | 'not_followed';

export interface EntryQuality {
  timing:       EntryTiming;
  priceQuality: PriceQuality;
  adherence:    EntryAdherence;
  /** Slippage from ideal entry in % */
  slippagePct:  number | null;
  notes:        string[];
}

export interface RiskAdherence {
  respectedInvalidation: boolean;
  violation:             boolean;
  /** Distance stop was breached (null = no breach) */
  breachAmount:          number | null;
  notes:                 string[];
}

export interface TargetAchievement {
  targetsHit:        number;
  maxTargetReached:  number | null;    // R multiple of the highest target reached
  /** 0–1: what fraction of the theoretical max R was captured */
  efficiency:        number | null;
  notes:             string[];
}

export interface MissedOpportunity {
  planId:           string;
  reason:           'no_entry' | 'late_entry' | 'ignored_signal';
  outcomePotential: string;
  notes:            string[];
}

export interface ExecutionScore {
  /** 0–100 composite score */
  overall: number;
  components: {
    entryQuality:       number;    // 0–100
    timing:             number;    // 0–100
    adherence:          number;    // 0–100
    outcomeAlignment:   number;    // 0–100
  };
  /** Named rules that contributed */
  appliedRules: string[];
}

export interface ExecutionFeedback {
  planId:     string;
  comparison: PlanOutcomeComparison;
  entry:      EntryQuality;
  risk:       RiskAdherence;
  targets:    TargetAchievement;
  missed:     MissedOpportunity | null;
  score:      ExecutionScore;
  /** Ordered, actionable guidance strings */
  guidance:   string[];
  /** Stable reasoning trace */
  reasoning:  string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function _planId(plan: TradePlan): string {
  return `${plan.symbol}:${plan.setupType}:${plan.direction}`;
}

function _priceDiffPct(a: number, b: number): number {
  if (b === 0) return 0;
  return Math.abs(a - b) / b;
}

function _round2(v: number): number { return Math.round(v * 100) / 100; }

// ── Step 1: Plan–outcome comparison ──────────────────────────────────────────

function _buildComparison(
  plan:  TradePlan,
  entry: JournalEntry | null,
): PlanOutcomeComparison {
  const planId    = _planId(plan);
  const deviation: string[] = [];

  // No journal entry = plan was not taken
  if (!entry) {
    return { planId, followed: false, outcome: 'missed', rMultiple: null, deviation: ['No journal entry linked — plan was not executed'] };
  }

  // Determine if the plan was followed
  const dirMatch = (plan.direction === 'long' && entry.dir === 'long') ||
                   (plan.direction === 'short' && entry.dir === 'short');
  if (!dirMatch) deviation.push(`Direction mismatch: plan=${plan.direction} actual=${entry.dir}`);

  // Check entry zone adherence
  if (entry.entry !== null) {
    const entryDiff = _priceDiffPct(entry.entry, plan.entryZone.idealEntry);
    if (entryDiff > 0.015) deviation.push(`Entry ${entry.entry} was ${(entryDiff * 100).toFixed(1)}% from plan ideal ${plan.entryZone.idealEntry}`);
  }

  // Outcome classification
  let outcome: OutcomeClassification = 'unknown';
  const rMult = entry.rMultiple;

  if (entry.outcome === 'win' && rMult !== null && rMult >= (plan.targets[0]?.rMultiple ?? 1.5)) {
    outcome = 'target_hit';
  } else if (entry.outcome === 'win' && rMult !== null) {
    outcome = 'partial';
  } else if (entry.outcome === 'loss') {
    outcome = 'stopped_out';
    deviation.push(`Stopped out at ${entry.exit ?? '?'} with ${rMult ?? '?'}R`);
  } else if (entry.outcome === 'breakeven') {
    outcome = 'partial';
  } else {
    outcome = 'unknown';
  }

  // Stop deviation
  if (entry.stop !== null && Math.abs(entry.stop - plan.risk.stopPrice) / plan.risk.stopPrice > 0.01) {
    deviation.push(`Stop set at ${entry.stop} vs plan stop ${plan.risk.stopPrice.toFixed(2)}`);
  }

  return {
    planId,
    followed: dirMatch && deviation.length === 0,
    outcome,
    rMultiple: rMult,
    deviation,
  };
}

// ── Step 2: Entry quality ─────────────────────────────────────────────────────

function _buildEntryQuality(
  plan:  TradePlan,
  entry: JournalEntry | null,
): EntryQuality {
  if (!entry || entry.entry === null) {
    return {
      timing: 'missed', priceQuality: 'neutral', adherence: 'not_followed',
      slippagePct: null,
      notes: ['No entry recorded — setup was not taken'],
    };
  }

  const actualEntry  = entry.entry;
  const idealEntry   = plan.entryZone.idealEntry;
  const zoneBottom   = plan.entryZone.zoneBottom;
  const zoneTop      = plan.entryZone.zoneTop;
  const dir          = plan.direction;
  const slippagePct  = _round2(_priceDiffPct(actualEntry, idealEntry) * 100);
  const notes: string[] = [];

  // Price quality: was entry inside or outside the zone?
  let priceQuality: PriceQuality;
  if (actualEntry >= zoneBottom && actualEntry <= zoneTop) {
    priceQuality = 'favorable';
    notes.push(`Entry ${actualEntry} was inside the planned zone [${zoneBottom}–${zoneTop}]`);
  } else if (_priceDiffPct(actualEntry, idealEntry) <= T.ENTRY_NEUTRAL_PCT) {
    priceQuality = 'neutral';
    notes.push(`Entry ${actualEntry} was near but outside the zone (${slippagePct}% slippage)`);
  } else {
    priceQuality = 'unfavorable';
    notes.push(`Entry ${actualEntry} was outside zone by ${slippagePct}% — unfavorable fill`);
  }

  // Timing: favorable = in zone; optimal = exact; late = chasing; early = too early
  let timing: EntryTiming;
  if (_priceDiffPct(actualEntry, idealEntry) <= T.ENTRY_OPTIMAL_PCT) {
    timing = 'optimal';
    notes.push('Entry timing was optimal — within 0.3% of ideal');
  } else if (
    (dir === 'long'  && actualEntry > idealEntry + idealEntry * T.ENTRY_LATE_PCT) ||
    (dir === 'short' && actualEntry < idealEntry - idealEntry * T.ENTRY_LATE_PCT)
  ) {
    timing = 'late';
    notes.push('Entry was late — price had moved beyond the ideal zone before entry');
  } else if (
    (dir === 'long'  && actualEntry < zoneBottom) ||
    (dir === 'short' && actualEntry > zoneTop)
  ) {
    timing = 'early';
    notes.push('Entry was early — entered before price confirmed the zone');
  } else {
    timing = 'optimal';
  }

  const adherence: EntryAdherence = priceQuality !== 'unfavorable' ? 'followed' : 'not_followed';

  return { timing, priceQuality, adherence, slippagePct, notes };
}

// ── Step 3: Risk adherence ────────────────────────────────────────────────────

function _buildRiskAdherence(
  plan:  TradePlan,
  entry: JournalEntry | null,
): RiskAdherence {
  const notes: string[] = [];

  if (!entry || entry.stop === null) {
    return { respectedInvalidation: false, violation: false, breachAmount: null, notes: ['No stop recorded'] };
  }

  const actualStop = entry.stop;
  const planStop   = plan.risk.stopPrice;
  const dir        = plan.direction;

  // Did the exit breach the invalidation level beyond tolerance?
  let violation    = false;
  let breachAmount: number | null = null;

  if (entry.exit !== null) {
    const exitBreachLong  = dir === 'long'  && entry.exit < planStop - planStop * T.STOP_BREACH_TOLERANCE;
    const exitBreachShort = dir === 'short' && entry.exit > planStop + planStop * T.STOP_BREACH_TOLERANCE;
    if (exitBreachLong || exitBreachShort) {
      violation    = true;
      breachAmount = _round2(Math.abs(entry.exit - planStop));
      notes.push(`Exit at ${entry.exit} breached plan stop ${planStop.toFixed(2)} by $${breachAmount}`);
    }
  }

  // Was the stop placed consistent with the plan?
  const stopDeviation = _priceDiffPct(actualStop, planStop);
  if (stopDeviation > 0.02) {
    notes.push(`Stop placed at ${actualStop} vs plan stop ${planStop.toFixed(2)} — ${(stopDeviation * 100).toFixed(1)}% deviation`);
  } else {
    notes.push(`Stop placement aligned with plan (${actualStop} vs plan ${planStop.toFixed(2)})`);
  }

  const respectedInvalidation = !violation && entry.outcome !== 'loss';

  return { respectedInvalidation, violation, breachAmount, notes };
}

// ── Step 4: Target achievement ────────────────────────────────────────────────

function _buildTargetAchievement(
  plan:  TradePlan,
  entry: JournalEntry | null,
): TargetAchievement {
  const notes: string[] = [];

  if (!entry || entry.exit === null) {
    return { targetsHit: 0, maxTargetReached: null, efficiency: null, notes: ['No exit recorded'] };
  }

  const exitPrice     = entry.exit;
  const entryPrice    = entry.entry ?? plan.entryZone.idealEntry;
  const dir           = plan.direction;
  const actualMove    = dir === 'long' ? exitPrice - entryPrice : entryPrice - exitPrice;
  const stopDist      = plan.risk.stopDistance;

  let targetsHit = 0;
  let maxTargetReached: number | null = null;

  for (const tgt of plan.targets) {
    const reached = dir === 'long' ? exitPrice >= tgt.price - tgt.price * T.TARGET_HIT_PCT
                                        : exitPrice <= tgt.price + tgt.price * T.TARGET_HIT_PCT;
    if (reached) {
      targetsHit++;
      maxTargetReached = tgt.rMultiple;
      notes.push(`T${targetsHit} (${tgt.price.toFixed(2)}, ${tgt.rMultiple}R) reached`);
    } else {
      notes.push(`T${plan.targets.indexOf(tgt) + 1} (${tgt.price.toFixed(2)}) not reached — exit was ${exitPrice.toFixed(2)}`);
      break;
    }
  }

  // Efficiency: what fraction of the max planned R was captured?
  const maxPlanRR = plan.targets[plan.targets.length - 1]?.rMultiple ?? 0;
  const actualRR  = stopDist > 0 ? actualMove / stopDist : null;
  const efficiency = (maxPlanRR > 0 && actualRR !== null)
    ? _round2(Math.min(1, Math.max(0, actualRR / maxPlanRR)))
    : null;

  if (efficiency !== null) {
    if (efficiency >= T.EFFICIENCY_EXCELLENT) notes.push(`Excellent capture efficiency: ${(efficiency * 100).toFixed(0)}%`);
    else if (efficiency >= T.EFFICIENCY_GOOD)  notes.push(`Good capture efficiency: ${(efficiency * 100).toFixed(0)}%`);
    else                                         notes.push(`Low capture efficiency: ${(efficiency * 100).toFixed(0)}% — consider trailing stops`);
  }

  return { targetsHit, maxTargetReached, efficiency, notes };
}

// ── Step 5: Missed opportunity ────────────────────────────────────────────────

function _buildMissedOpportunity(
  plan:       TradePlan,
  entry:      JournalEntry | null,
  comparison: PlanOutcomeComparison,
): MissedOpportunity | null {
  if (entry !== null && comparison.followed) return null;

  const planId = _planId(plan);
  const maxPotential = plan.targets[0] ? `${plan.targets[0].rMultiple}R potential to T1` : 'unknown potential';

  if (!entry) {
    return {
      planId,
      reason: 'no_entry',
      outcomePotential: maxPotential,
      notes: ['Setup met plan criteria but no trade was recorded', `Plan quality was ${plan.quality.tier} (${(plan.quality.overall * 100).toFixed(0)}%)`],
    };
  }

  if (comparison.outcome === 'missed') {
    return {
      planId,
      reason: 'ignored_signal',
      outcomePotential: maxPotential,
      notes: [`Signal fired (evidence: ${plan.quality.overall * 100}%) but plan was not executed`],
    };
  }

  return null;
}

// ── Step 6: Execution score ───────────────────────────────────────────────────

function _buildScore(
  entryQ:  EntryQuality,
  risk:    RiskAdherence,
  targets: TargetAchievement,
  comp:    PlanOutcomeComparison,
): ExecutionScore {
  const rules: string[] = [];

  // Entry quality score
  const entryQScore = entryQ.adherence === 'not_followed' ? 20
    : entryQ.priceQuality === 'favorable' ? 90
    : entryQ.priceQuality === 'neutral'   ? 65
    : 35;
  rules.push(`ENTRY_${entryQ.priceQuality.toUpperCase()}(${entryQScore})`);

  // Timing score
  const timingScore = entryQ.timing === 'optimal' ? 95
    : entryQ.timing === 'early'   ? 65
    : entryQ.timing === 'late'    ? 45
    : 10;
  rules.push(`TIMING_${entryQ.timing.toUpperCase()}(${timingScore})`);

  // Adherence score
  const adherenceScore = risk.violation ? 20
    : risk.respectedInvalidation ? 90
    : 60;
  rules.push(`RISK_${risk.violation ? 'VIOLATED' : 'RESPECTED'}(${adherenceScore})`);

  // Outcome alignment score
  const outcomeScore = comp.outcome === 'target_hit'   ? 100
    : comp.outcome === 'partial'                        ? 70
    : comp.outcome === 'stopped_out' && !risk.violation ? 60
    : comp.outcome === 'stopped_out' && risk.violation  ? 25
    : comp.outcome === 'missed'                         ? 0
    : 50;
  rules.push(`OUTCOME_${comp.outcome.toUpperCase()}(${outcomeScore})`);

  // Efficiency bonus (up to +5 on overall)
  const effBonus = targets.efficiency !== null ? targets.efficiency * 5 : 0;

  const overall = Math.round(Math.min(100,
    entryQScore  * T.W_ENTRY_QUALITY     +
    timingScore  * T.W_TIMING            +
    adherenceScore * T.W_ADHERENCE       +
    outcomeScore * T.W_OUTCOME_ALIGNMENT +
    effBonus
  ));

  return {
    overall,
    components: {
      entryQuality:     entryQScore,
      timing:           timingScore,
      adherence:        adherenceScore,
      outcomeAlignment: outcomeScore,
    },
    appliedRules: rules,
  };
}

// ── Step 7: Guidance generation ───────────────────────────────────────────────

function _buildGuidance(
  entryQ:   EntryQuality,
  risk:     RiskAdherence,
  targets:  TargetAchievement,
  comp:     PlanOutcomeComparison,
  missed:   MissedOpportunity | null,
  score:    ExecutionScore,
): string[] {
  const guidance: string[] = [];

  // Entry guidance
  if (entryQ.timing === 'late') {
    guidance.push('Entry was late — best entries occur at the first test of the entry zone, not after confirmation is obvious');
  }
  if (entryQ.timing === 'early') {
    guidance.push('Entry was early — wait for price to enter the planned zone before committing');
  }
  if (entryQ.priceQuality === 'unfavorable') {
    guidance.push('Entry price was outside the planned zone — use limit orders at the zone rather than market orders');
  }

  // Risk guidance
  if (risk.violation) {
    guidance.push('Invalidation level was breached — avoid holding past a confirmed structure break; a loss taken at plan stop is better than a larger unplanned loss');
  }
  if (comp.deviation.some(d => d.includes('Stop set at'))) {
    guidance.push('Stop placement deviated from the plan — pre-set stop at the plan level before entry to avoid emotional adjustments');
  }

  // Target guidance
  if (targets.efficiency !== null && targets.efficiency < T.EFFICIENCY_GOOD) {
    guidance.push('Capture efficiency was low — consider using a trailing stop after T1 to let winners run further');
  }
  if (targets.targetsHit === 0 && comp.outcome !== 'stopped_out') {
    guidance.push('No targets were hit — review whether position was held long enough or exit criteria were applied too conservatively');
  }

  // Missed opportunity guidance
  if (missed?.reason === 'no_entry') {
    guidance.push('Setup was valid but not taken — review this signal type and consider logging why the plan was passed on');
  }
  if (missed?.reason === 'ignored_signal') {
    guidance.push('Setup signal fired with sufficient quality but was ignored — improve attention to real-time signal triggers');
  }

  // Positive reinforcement
  if (score.overall >= 85) {
    guidance.push('Excellent execution — entry, risk, and outcome all aligned well with the plan');
  } else if (score.overall >= 70) {
    guidance.push('Good execution — minor improvements to entry timing or target capture would improve overall score');
  }

  if (guidance.length === 0) {
    guidance.push('Insufficient data to generate specific guidance — ensure entry, stop, and exit are logged in the journal');
  }

  return guidance;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Evaluate execution quality by comparing a TradePlan with a JournalEntry.
 *
 * PURE FUNCTION — no Date.now(), no I/O, deterministic.
 *
 * @param plan  — TradePlan from buildExecutionPlan()
 * @param entry — linked JournalEntry (matched by operator), or null if not taken
 */
export function evaluateExecution(
  plan:  TradePlan,
  entry: JournalEntry | null,
): ExecutionFeedback {
  const comparison = _buildComparison(plan, entry);
  const entryQ     = _buildEntryQuality(plan, entry);
  const risk       = _buildRiskAdherence(plan, entry);
  const targets    = _buildTargetAchievement(plan, entry);
  const missed     = _buildMissedOpportunity(plan, entry, comparison);
  const score      = _buildScore(entryQ, risk, targets, comparison);
  const guidance   = _buildGuidance(entryQ, risk, targets, comparison, missed, score);

  const reasoning: string[] = [
    `Plan: ${_planId(plan)} | Quality: ${plan.quality.tier} (${(plan.quality.overall * 100).toFixed(0)}%)`,
    `Outcome: ${comparison.outcome} | Followed: ${comparison.followed}`,
    `Entry: ${entryQ.timing}/${entryQ.priceQuality} | Adherence: ${entryQ.adherence}`,
    `Risk: respected=${risk.respectedInvalidation} | violation=${risk.violation}`,
    `Targets hit: ${targets.targetsHit} | Efficiency: ${targets.efficiency !== null ? (targets.efficiency * 100).toFixed(0) + '%' : 'N/A'}`,
    `Execution score: ${score.overall}/100`,
    `Rules: ${score.appliedRules.join(', ')}`,
  ];

  return {
    planId:     _planId(plan),
    comparison,
    entry:      entryQ,
    risk,
    targets,
    missed,
    score,
    guidance,
    reasoning,
  };
}

/**
 * Summarize execution feedback across multiple plan–entry pairs.
 * PURE FUNCTION — deterministic.
 */
export function summarizeExecutionFeedback(feedbacks: ExecutionFeedback[]): {
  count:           number;
  avgScore:        number | null;
  followedRate:    number | null;
  targetsHitRate:  number | null;
  topIssues:       string[];
  topStrengths:    string[];
} {
  if (feedbacks.length === 0) {
    return { count: 0, avgScore: null, followedRate: null, targetsHitRate: null, topIssues: [], topStrengths: [] };
  }

  const avg        = (arr: number[]) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const avgScore   = Math.round(avg(feedbacks.map(f => f.score.overall)));
  const followed   = feedbacks.filter(f => f.comparison.followed).length;
  const withTarget = feedbacks.filter(f => f.comparison.outcome !== 'missed' && f.targets.targetsHit > 0).length;
  const actedOn    = feedbacks.filter(f => f.comparison.outcome !== 'missed').length;

  const issueCounts: Record<string, number> = {};
  const strengthCounts: Record<string, number> = {};

  for (const fb of feedbacks) {
    if (fb.entry.timing === 'late')               issueCounts['Late entries']            = (issueCounts['Late entries'] ?? 0) + 1;
    if (fb.entry.priceQuality === 'unfavorable')  issueCounts['Unfavorable entry fills'] = (issueCounts['Unfavorable entry fills'] ?? 0) + 1;
    if (fb.risk.violation)                        issueCounts['Risk violations']          = (issueCounts['Risk violations'] ?? 0) + 1;
    if (fb.targets.efficiency !== null && fb.targets.efficiency < T.EFFICIENCY_GOOD)
                                                   issueCounts['Low capture efficiency']  = (issueCounts['Low capture efficiency'] ?? 0) + 1;
    if (fb.comparison.outcome === 'missed')        issueCounts['Missed setups']           = (issueCounts['Missed setups'] ?? 0) + 1;

    if (fb.entry.timing === 'optimal')             strengthCounts['Precise entry timing']   = (strengthCounts['Precise entry timing'] ?? 0) + 1;
    if (fb.risk.respectedInvalidation)             strengthCounts['Risk discipline']         = (strengthCounts['Risk discipline'] ?? 0) + 1;
    if (fb.comparison.outcome === 'target_hit')    strengthCounts['Target achievement']      = (strengthCounts['Target achievement'] ?? 0) + 1;
  }

  const topIssues    = Object.entries(issueCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k, v]) => `${k} (${v})`);
  const topStrengths = Object.entries(strengthCounts).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([k, v]) => `${k} (${v})`);

  return {
    count: feedbacks.length,
    avgScore,
    followedRate:   feedbacks.length > 0 ? Math.round(followed / feedbacks.length * 100) / 100 : null,
    targetsHitRate: actedOn > 0 ? Math.round(withTarget / actedOn * 100) / 100 : null,
    topIssues,
    topStrengths,
  };
}
